import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import type { WorkOrderChangeOtherPriceData, WorkOrderChangeSeasonData, WorkOrderPriceStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import { percentCellFormatter } from 'pages/Project/BimProject/BimCommonComponents/WorkOrderPriceCellRenderers';
import { badgePropsByStatus } from 'pages/Project/Common/CodeCell';
import { destroyAgGridTableFilters, getCheckedIdsFromObjValues } from 'utils';

import type { ColDef } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
    WorkOrderChangeCodeRenderer,
    WorkOrderChangeHeaderCellWithTooltip,
    WorkOrderChangeMenuRenderer,
    WorkOrderChangeNumericCell,
    WorkOrderChangeStatusRenderer,
} from '../CellRenderers';
import {
    COST_COLUMNS_WIDTH,
    STYLES_FOR_CELL_WITH_WRAP,
    totalCostText,
    totalCostTooltipText,
    VOLUME_OTHER_COLUMNS_WIDTH,
} from '../constants';
import {
    workOrderChangeOtherPriceNameFilterGql,
    workOrderChangeOtherPriceStatusFilterGql,
} from '../WorkOrderChangeQueries';

import { useWorkOrderChangeOtherSeasonsColumns } from './useWorkOrderChangeOtherSeasonsColumns';

interface IUseWorkOrderChangeOtherColumns {
    workOrderChangeId: number;
    isActionsDisabled: boolean;
    workOrderObjectId: number | undefined;
    workOrderChangeObjectId: number | undefined;
    gridApiRef: AgGridReact<any>;
    changeFilterHandler: ({ fieldName, checkedValues }: { fieldName: string; checkedValues: any }) => void;
    workOrderChangeUerRules: IUseAccess['workOrderChangeUer'];
    costRounding: number;
    workOrderChangeSeasons: WorkOrderChangeSeasonData[];
}

export const useWorkOrderChangeOtherColumns = (props: IUseWorkOrderChangeOtherColumns) => {
    const {
        workOrderChangeId,
        isActionsDisabled,
        workOrderObjectId,
        workOrderChangeObjectId,
        gridApiRef,
        changeFilterHandler,
        workOrderChangeUerRules,
        costRounding,
        workOrderChangeSeasons,
    } = props;

    const filterVariables = useMemo(
        () => ({
            workOrderChangeId,
            workOrderObjectId,
            workOrderChangeObjectId,
        }),
        [workOrderChangeId, workOrderChangeObjectId, workOrderObjectId],
    );

    const seasonColumns = useWorkOrderChangeOtherSeasonsColumns({
        costRounding,
        workOrderChangeSeasons,
    });

    const [checkedRows, setCheckedRows] = useState({});
    const [isSingleDeleteModalOpened, setIsSingleDeleteModalOpened] = useState(false);
    const [isEditDistributionOpened, setIsEditDistributionOpened] = useState(false);
    const [isEditOtherOpened, setIsEditOtherOpenedOpened] = useState(false);
    const [isViewOtherModalOpened, setIsViewOtherModalOpened] = useState(false);
    const [isEditNoteModalOpened, setIsEditNoteModalOpened] = useState(false);
    const [rowData, setRowData] = useState();

    const checkedRowsArr = useMemo(
        () => getCheckedIdsFromObjValues(['workOrderChangeOtherPriceId', 'workOrderOtherPriceId'])(checkedRows),
        [checkedRows],
    );

    const checkedOtherRow = useMemo(
        () => R.pipe(R.pickBy(R.identity), R.values)(checkedRows)[0],
        [checkedRows],
    ) as WorkOrderChangeOtherPriceData;

    const checkRowHandler = useCallback(({ id, data }) => {
        setCheckedRows((prev) => ({ ...prev, [id]: data }));
    }, []);

    const checkSingleRowHandler = useCallback(({ id, data }) => {
        setCheckedRows({ [id]: data });
    }, []);

    const openSingleDeleteModalHandler = useCallback(() => {
        setIsSingleDeleteModalOpened(true);
    }, []);

    const closeSingleDeleteModalHandler = useCallback(() => {
        setIsSingleDeleteModalOpened(false);
        setCheckedRows({});
    }, []);

    const openEditDistributionModalHandler = useCallback(() => {
        setIsEditDistributionOpened(true);
    }, []);

    const closeEditDistributionModalHandler = useCallback(() => {
        setIsEditDistributionOpened(false);
        setCheckedRows({});
    }, []);

    const openEditOtherModalHandler = useCallback(() => {
        setIsEditOtherOpenedOpened(true);
    }, []);

    const openViewOtherModalHandler = useCallback(() => {
        setIsViewOtherModalOpened(true);
    }, []);

    const closeEditOtherModalHandler = useCallback(() => {
        setIsEditOtherOpenedOpened(false);
        setCheckedRows({});
    }, []);

    const closeViewOtherModalHandler = useCallback(() => {
        setIsViewOtherModalOpened(false);
        setCheckedRows({});
    }, []);

    const resetCheckedRows = useCallback(() => {
        setCheckedRows({});
    }, []);

    const openEditNoteModalHandler = useCallback(() => {
        setIsEditNoteModalOpened(true);
    }, []);

    const closeEditNoteModalHandler = useCallback(() => {
        setIsEditNoteModalOpened(false);
    }, []);

    const columns = useMemo<ColDef[]>(
        () => [
            {
                headerName: '',
                field: '',
                width: 80,
                cellRenderer: WorkOrderChangeMenuRenderer({
                    checkRowHandler,
                    checkedRows,
                    checkSingleRowHandler,
                    openSingleDeleteModalHandler,
                    openEditDistributionModalHandler,
                    openEditOtherModalHandler,
                    openViewOtherModalHandler,
                    isActionsDisabled,
                    workOrderChangeUerRules,
                    openEditNoteModalHandler,
                    setRowData,
                    isOpenMoveVolumesBetweenSeasonsForUerDisabled: workOrderChangeSeasons.length <= 1,
                }),
                cellClass: 'centeredAgGridCell firstColumn',
                spanHeaderHeight: true,
                pinned: 'left',
                lockPosition: 'left',
            },
            {
                headerName: 'Статус',
                field: 'status',
                width: 105,
                cellRenderer: WorkOrderChangeStatusRenderer,
                cellClass: 'centeredAgGridCell',
                spanHeaderHeight: true,
                filter: AgGridCustomFilterAdapter,
                tooltipComponent: undefined,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderChangeOtherPriceStatusFilter',
                    query: workOrderChangeOtherPriceStatusFilterGql,
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            checkboxSelection: true,
                            headerName: 'Статус',
                            field: 'value',
                            lockVisible: true,
                            flex: 1,
                            cellRenderer: ({ value }: { value: WorkOrderPriceStatusEnum }) =>
                                badgePropsByStatus[value].label,
                        },
                    ],

                    changeFilterHandler,
                },
            },
            {
                headerName: 'Шифр',
                field: 'code',
                cellRenderer: WorkOrderChangeCodeRenderer,
                width: 80,
                cellClass: 'centeredAgGridCell',
                spanHeaderHeight: true,
            },
            {
                headerName: 'Наименование ',
                field: 'name',
                resizable: true,
                width: 350,
                wrapText: true,
                autoHeight: true,
                cellStyle: STYLES_FOR_CELL_WITH_WRAP,
                spanHeaderHeight: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: true,
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderChangeOtherPriceNameFilter',
                    query: workOrderChangeOtherPriceNameFilterGql,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            checkboxSelection: true,
                            headerName: 'Наименование',
                            field: 'value',
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Ед.изм',
                field: 'calculatingTypeValue',
                valueFormatter: (data) => percentCellFormatter(data, 2),
                width: 85,
                spanHeaderHeight: true,
            },
            {
                headerName: 'План по НЗ',
                marryChildren: true,
                children: [
                    {
                        headerName: 'Объем всего',
                        field: 'totalPlanVolume',
                        width: VOLUME_OTHER_COLUMNS_WIDTH,
                    },
                    {
                        headerName: 'Стоимость всего, руб. без НДС',
                        field: 'totalPlanCost',
                        headerComponent: WorkOrderChangeHeaderCellWithTooltip({
                            text: totalCostText,
                            tooltipText: totalCostTooltipText,
                        }),
                        width: COST_COLUMNS_WIDTH,
                        cellRenderer: WorkOrderChangeNumericCell({ key: 'totalPlanCost', decimal: costRounding }),
                    },
                ],
            },
            {
                headerName: 'Выполнено по НЗ',
                marryChildren: true,
                children: [
                    {
                        headerName: 'Объем всего',
                        field: 'totalCompletedVolume',
                        width: VOLUME_OTHER_COLUMNS_WIDTH,
                    },
                    {
                        headerName: 'Стоимость всего, руб. без НДС',
                        field: 'totalCompletedCost',
                        headerComponent: WorkOrderChangeHeaderCellWithTooltip({
                            text: totalCostText,
                            tooltipText: totalCostTooltipText,
                        }),
                        width: COST_COLUMNS_WIDTH,
                        cellRenderer: WorkOrderChangeNumericCell({ key: 'totalCompletedCost', decimal: costRounding }),
                    },
                ],
            },
            {
                headerName: 'Остаток по НЗ',
                marryChildren: true,
                children: [
                    {
                        headerName: 'Объем всего',
                        field: 'totalRemainderVolume',
                        width: VOLUME_OTHER_COLUMNS_WIDTH,
                    },
                    {
                        headerName: 'Стоимость всего, руб. без НДС',
                        field: 'totalRemainderCost',
                        headerComponent: WorkOrderChangeHeaderCellWithTooltip({
                            text: totalCostText,
                            tooltipText: totalCostTooltipText,
                        }),
                        width: COST_COLUMNS_WIDTH,
                        cellRenderer: WorkOrderChangeNumericCell({ key: 'totalRemainderCost', decimal: costRounding }),
                    },
                ],
            },
            ...seasonColumns,
        ],
        [
            changeFilterHandler,
            checkRowHandler,
            checkSingleRowHandler,
            checkedRows,
            costRounding,
            filterVariables,
            isActionsDisabled,
            openEditDistributionModalHandler,
            openEditNoteModalHandler,
            openEditOtherModalHandler,
            openSingleDeleteModalHandler,
            openViewOtherModalHandler,
            seasonColumns,
            workOrderChangeSeasons.length,
            workOrderChangeUerRules,
        ],
    );

    const [selection, , , setSelection] = useSelection('workOrderChangeOtherPriceTable');
    const [pinnedItems, , , setPinned] = usePin('workOrderChangeOtherPriceTable');

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                if (idx > 1) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean };

                    if (idx < 6) {
                        newColumn.disabled = true;
                    }

                    if (pinnedItems[getColumnId(column)]) {
                        newColumn.pinned = true;
                    }

                    acc.push(newColumn);
                }

                return acc;
            }, []),
        [columns, pinnedItems],
    );

    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    const handleTableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                setSelection,
                setPinned,
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
            });
        },
        [fullSetSettingsColumns, setPinned, setSelection],
    );

    useEffect(() => {
        destroyAgGridTableFilters({
            api: gridApiRef?.api,
            columns: ['name', 'status'],
        });
    }, [gridApiRef?.api]);

    return {
        fullSetColumns,
        checkedRows,
        checkedRowsArr,
        checkedOtherRow,
        resetCheckedRows,
        isSingleDeleteModalOpened,
        closeSingleDeleteModalHandler,
        isEditDistributionOpened,
        closeEditDistributionModalHandler,
        isViewOtherModalOpened,
        closeViewOtherModalHandler,
        fullSetSettingsColumns,
        handleTableSettingsConfirm,
        isColumnVisible,
        isEditOtherOpened,
        closeEditOtherModalHandler,
        rowData,
        openEditNoteModalHandler,
        closeEditNoteModalHandler,
        isEditNoteModalOpened,
    };
};
